import { useEffect, useMemo, useRef, useState } from 'react';
import type { JSX, ReactNode } from 'react';
import type { AnnouncementLayout, AnnouncementOpenMode, AnnouncementPosition } from '@chirpback/protocol';
import type { ShellHydration } from '../../api.js';
import {
  buildEventMeta,
  fetchPublicAnnouncement,
  fetchPublicFeed,
  resolveEventIdentity,
  trackAnnouncementEvent,
} from '../../api.js';
import type { HostBridge } from '../../host.js';
import { BannerShell } from './_components/shells/BannerShell.js';
import { CompactBannerBody } from './_components/CompactBannerBody.js';
import { DrawerShell } from './_components/shells/DrawerShell.js';
import { FeedView } from './_components/FeedView.js';
import { ModalShell } from './_components/shells/ModalShell.js';
import { SingleAnnouncementBody } from './_components/SingleAnnouncementBody.js';
import { ToastShell } from './_components/shells/ToastShell.js';
import { resolveAnnouncementPalette, type AnnouncementShape } from './types.js';

// Module cache: fresh hits skip the loader entirely, stale hits render now and refresh in the background.
interface CachedFeed {
  result: { announcements: AnnouncementShape[]; title: string | null; subtitle: string | null };
  fetchedAt: number;
}
const feedCache = new Map<string, CachedFeed>();
const FRESH_MS = 2 * 60 * 1000;
const feedKey = (publicId: string, collectionSlug: string | null): string =>
  `${publicId}::${collectionSlug ?? 'all'}`;

// Shared shape for layoutSettings.styles — same fields the standalone embed
// mode (EmbedStyles) supports, so a host passing these via Chirp.changelog.open
// gets the same drawer theme as a Chirp.embed.announcements consumer.
type LayoutStyles = {
  background?: string;
  text?: string;
  textMuted?: string;
  primary?: string;
  border?: string;
  radius?: number;
  fontFamily?: string;
};

// Merge host-supplied layoutSettings.styles onto a base palette. Used by every
// renderer (SingleRenderer, FeedRenderer, InlineFeedRenderer) so drawer, modal,
// banner, toast, and inline embed all honor the same host overrides.
function applyLayoutStyles(
  basePalette: ReturnType<typeof resolveAnnouncementPalette>,
  layoutSettings: Record<string, unknown> | undefined,
): ReturnType<typeof resolveAnnouncementPalette> {
  const styles = layoutSettings?.styles as LayoutStyles | undefined;
  if (!styles) return basePalette;
  return {
    ...basePalette,
    ...(styles.background ? { background: styles.background, card: styles.background } : {}),
    ...(styles.text ? { text: styles.text } : {}),
    ...(styles.textMuted ? { textMuted: styles.textMuted } : {}),
    ...(styles.primary ? { primary: styles.primary, primarySoft: styles.primary + '1f' } : {}),
    ...(styles.border ? { border: styles.border } : {}),
    ...(styles.radius != null ? { radius: styles.radius, radiusLg: styles.radius } : {}),
    ...(styles.fontFamily ? { font: styles.fontFamily } : {}),
  };
}

interface AnnouncementRendererProps {
  bridge: HostBridge;
  mode: AnnouncementOpenMode;
  publicId?: string;
  collectionSlug?: string;
  layout?: AnnouncementLayout;
  position?: AnnouncementPosition;
  layoutSettings?: Record<string, unknown>;
  prefetched?: unknown;
  hydration: ShellHydration;
}

export function AnnouncementRenderer(props: AnnouncementRendererProps): JSX.Element | null {
  if (props.layout === 'inline') {
    return <InlineFeedRenderer {...props} />;
  }
  if (props.mode === 'collection' || props.mode === 'global') {
    return <FeedRenderer {...props} />;
  }
  return <SingleRenderer {...props} />;
}

function SingleRenderer({
  bridge,
  publicId,
  layout,
  position,
  layoutSettings,
  prefetched,
  hydration,
}: AnnouncementRendererProps): JSX.Element | null {
  const [announcement, setAnnouncement] = useState<AnnouncementShape | null>(
    (prefetched as AnnouncementShape | undefined) ?? null,
  );
  const [notFound, setNotFound] = useState(false);
  const palette = useMemo(
    () => applyLayoutStyles(resolveAnnouncementPalette(announcement?.theme), layoutSettings),
    [announcement, layoutSettings],
  );
  // Resolve layout sync from prop or prefetched payload; otherwise wait for fetch to avoid flicker.
  const initialLayout =
    layout ??
    (announcement ? resolveLayoutFromAnnouncement(announcement) : null);

  // Banner/toast render as compact bars; expanding swaps to the modal/drawer the admin picked.
  const [expandedTo, setExpandedTo] = useState<AnnouncementLayout | null>(null);
  const resolvedLayout: AnnouncementLayout | null = expandedTo ?? initialLayout;
  const shownRef = useRef(false);
  // Dwell timer for modal/drawer direct opens — banner/toast get "seen" from the expand click.
  const dwellSeenFiredRef = useRef(false);
  const dwellTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const DIRECT_OPEN_SEEN_DWELL_MS = 3000;
  // Same rationale as the survey shell: once dismissed, suppress repeat
  // dismiss emits during the teardown window.
  const dismissedRef = useRef(false);

  useEffect(() => {
    if (!publicId) return;
    if (announcement) return; // already had a prefetched payload
    void fetchPublicAnnouncement(hydration, publicId).then((data) => {
      if (data) {
        setAnnouncement(data as AnnouncementShape);
      } else {
        // Mirror the survey error path so customers see a graceful inline
        // state instead of an empty mounted iframe. Emits an `error` event on
        // the parent and renders the StatusView in this iframe.
        setNotFound(true);
        bridge.send('chirp:error', {
          code: 'announcement_not_found',
          message: `Announcement ${publicId} is not available`,
        });
      }
    }).catch((err: Error) => {
      setNotFound(true);
      bridge.send('chirp:error', {
        code: 'announcement_fetch',
        message: err.message,
      });
    });
  }, [hydration, publicId, announcement, bridge]);

  useEffect(() => {
    if (!announcement || shownRef.current) return;
    shownRef.current = true;
    bridge.send('chirp:announcement:shown', {
      id: announcement.publicId ?? publicId ?? '',
      internalId: announcement._id,
    });
    if (announcement._id) {
      void trackAnnouncementEvent(hydration, {
        announcementId: announcement._id,
        eventType: 'shown',
        ...resolveEventIdentity(hydration),
        meta: buildEventMeta(hydration),
      });
    }
  }, [announcement, bridge, hydration, publicId]);

  // Direct-open modal/drawer fires 'seen' after 3s dwell; faster close = 'dismissed'.
  useEffect(() => {
    if (!announcement || !resolvedLayout) return;
    // Slideout normalizes to drawer upstream.
    const isDirectFullSurface =
      expandedTo === null &&
      (resolvedLayout === 'modal' || resolvedLayout === 'drawer');
    if (!isDirectFullSurface) return;
    if (dwellSeenFiredRef.current) return;

    dwellTimerRef.current = setTimeout(() => {
      if (announcement._id) {
        void trackAnnouncementEvent(hydration, {
          announcementId: announcement._id,
          eventType: 'seen',
          ...resolveEventIdentity(hydration),
          meta: buildEventMeta(hydration),
        });
      }
      dwellSeenFiredRef.current = true;
    }, DIRECT_OPEN_SEEN_DWELL_MS);

    return () => {
      if (dwellTimerRef.current) {
        clearTimeout(dwellTimerRef.current);
        dwellTimerRef.current = null;
      }
    };
  }, [announcement, resolvedLayout, expandedTo, hydration]);

  const dismiss = (reason = 'user_close'): void => {
    if (dismissedRef.current) return;
    dismissedRef.current = true;
    // 'dismissed' only fires for an un-acknowledged close: bar X, or modal/drawer shut before 3s.
    bridge.send('chirp:announcement:dismissed', {
      id: announcement?.publicId ?? publicId ?? '',
      internalId: announcement?._id,
      reason,
    });

    if (dwellTimerRef.current) {
      clearTimeout(dwellTimerRef.current);
      dwellTimerRef.current = null;
    }

    if (!announcement?._id) return;

    const isCompactBar = resolvedLayout === 'banner' || resolvedLayout === 'toast';
    const wasExpandedFromBar = expandedTo !== null;
    const isDirectFullSurface =
      !isCompactBar && !wasExpandedFromBar;

    const shouldRecordDismissed =
      (isCompactBar && expandedTo === null) ||
      (isDirectFullSurface && !dwellSeenFiredRef.current);

    if (shouldRecordDismissed) {
      void trackAnnouncementEvent(hydration, {
        announcementId: announcement._id,
        eventType: 'dismissed',
        ...resolveEventIdentity(hydration),
        meta: buildEventMeta(hydration),
      });
    }
  };

  const onCtaClick = (): void => {
    bridge.send('chirp:announcement:clicked', {
      id: announcement?.publicId ?? publicId ?? '',
      internalId: announcement?._id,
      ctaUrl: announcement?.cta?.url,
    });
    if (announcement?._id) {
      void trackAnnouncementEvent(hydration, {
        announcementId: announcement._id,
        eventType: 'clicked',
        ...resolveEventIdentity(hydration),
        meta: buildEventMeta(hydration),
      });
    }
  };

  const expand = (): void => {
    // Expanding from a compact bar counts as a click on the announcement itself.
    // Closing the expanded view afterward fires no further event.
    if (announcement?._id) {
      void trackAnnouncementEvent(hydration, {
        announcementId: announcement._id,
        eventType: 'clicked',
        ...resolveEventIdentity(hydration),
        meta: buildEventMeta(hydration),
      });
    }
    const expandStyle = (layoutSettings?.expandStyle as string | undefined) ?? 'modal';
    setExpandedTo(expandStyle === 'drawer' ? 'drawer' : 'modal');
  };

  // Not-found surfaces an inline error in a modal so the user can dismiss it.
  // Without this, an invalid announcement id mounts an empty surface forever.
  if (notFound) {
    return renderInShell({
      layout: 'modal',
      position,
      palette,
      bridge,
      onDismiss: () => dismiss('not_available'),
      children: (
        <div style={{ padding: '32px 28px', textAlign: 'center', color: palette.textMuted }}>
          <p style={{ margin: 0, fontSize: 15, color: palette.text }}>
            This announcement is no longer available.
          </p>
        </div>
      ),
    });
  }

  // Wait for layout to resolve before mounting — avoids modal→drawer flicker.
  if (!resolvedLayout) return null;

  const isCompactBar = resolvedLayout === 'banner' || resolvedLayout === 'toast';
  const body = isCompactBar ? (
    <CompactBannerBody
      palette={palette}
      announcement={announcement}
      onExpand={expand}
      onDismiss={() => dismiss()}
      onCtaClick={onCtaClick}
      variant={resolvedLayout === 'toast' ? 'stack' : 'inline'}
    />
  ) : (
    <SingleAnnouncementBody
      palette={palette}
      announcement={announcement}
      compact={false}
      onDismiss={() => dismiss()}
      onCtaClick={onCtaClick}
    />
  );

  return renderInShell({
    layout: resolvedLayout,
    position,
    palette,
    bridge,
    onDismiss: () => dismiss(),
    children: body,
  });
}

function FeedRenderer({
  bridge,
  mode,
  publicId,
  collectionSlug,
  layout,
  position,
  layoutSettings,
  hydration,
}: AnnouncementRendererProps): JSX.Element | null {
  const [activeSlug, setActiveSlug] = useState<string | null>(
    mode === 'collection' ? collectionSlug ?? null : null,
  );
  // Global changelog has no publicId — use the project id so the backend resolves the global feed.
  const effectiveKey = publicId ?? (mode === 'global' ? hydration.projectId : '');
  const initialCached = effectiveKey ? feedCache.get(feedKey(effectiveKey, activeSlug)) : undefined;
  const [announcements, setAnnouncements] = useState<AnnouncementShape[]>(
    initialCached?.result.announcements ?? [],
  );
  const [feedTitle, setFeedTitle] = useState<string | null>(initialCached?.result.title ?? null);
  const [feedSubtitle, setFeedSubtitle] = useState<string | null>(initialCached?.result.subtitle ?? null);
  const [selected, setSelected] = useState<AnnouncementShape | null>(null);
  // Skeleton only when nothing is cached; stale cache renders immediately and refreshes silently.
  const [loading, setLoading] = useState(!initialCached);
  // Once-per-mount guards. Without these, a hydration ref update or React
  // StrictMode would re-fire shown/changelog:shown.
  const feedShownEmittedRef = useRef(false);
  const feedDismissedRef = useRef(false);

  // Emit a shown event so customers can subscribe to "feed visible". Gated on
  // actual content — a feed with zero items (invalid id, empty collection) is
  // a phantom open and shouldn't fire shown. For invalid ids we surface an
  // error event instead. For empty-but-valid feeds we render the FeedView's
  // empty state without firing shown — analytics shouldn't count zero-content
  // opens as impressions.
  useEffect(() => {
    if (feedShownEmittedRef.current || !effectiveKey) return;
    if (loading) return;
    if (announcements.length === 0) return;
    feedShownEmittedRef.current = true;
    if (mode === 'global') {
      bridge.send('chirp:changelog:shown', undefined as never);
    } else {
      bridge.send('chirp:announcement:shown', {
        id: `feed_${effectiveKey}`,
      });
    }
  }, [bridge, effectiveKey, mode, loading, announcements.length]);

  // Surface an error for an empty collection that we explicitly opened by id.
  // Global changelog (mode='global') with no items is a valid empty feed.
  const feedErrorEmittedRef = useRef(false);
  useEffect(() => {
    if (feedErrorEmittedRef.current || !effectiveKey) return;
    if (loading) return;
    if (mode === 'global') return;
    if (announcements.length > 0) return;
    feedErrorEmittedRef.current = true;
    bridge.send('chirp:error', {
      code: 'collection_not_found',
      message: `Collection ${effectiveKey} has no announcements`,
    });
  }, [bridge, effectiveKey, mode, loading, announcements.length]);

  useEffect(() => {
    if (!effectiveKey) return;
    const key = feedKey(effectiveKey, activeSlug);
    const cached = feedCache.get(key);
    const isFresh = cached && Date.now() - cached.fetchedAt < FRESH_MS;

    if (cached) {
      setAnnouncements(cached.result.announcements);
      setFeedTitle(cached.result.title);
      setFeedSubtitle(cached.result.subtitle);
      setLoading(false);
    } else {
      setLoading(true);
    }

    if (isFresh) return;

    // Identity hints let the backend surface segment-targeted items this user matches.
    const identityHints = {
      email: hydration.user?.email,
      uniqueId: hydration.user?.uniqueId,
    };
    const feedInput = publicId
      ? { publicId, collectionSlug: activeSlug ?? undefined, limit: 50, ...identityHints }
      : { projectId: hydration.projectId, collectionSlug: activeSlug ?? undefined, limit: 50, ...identityHints };

    void fetchPublicFeed(hydration, feedInput)
      .then((result) => {
        const next = {
          announcements: (result.announcements ?? []) as AnnouncementShape[],
          title: result.title ?? null,
          subtitle: result.subtitle ?? null,
        };
        feedCache.set(key, { result: next, fetchedAt: Date.now() });
        setAnnouncements(next.announcements);
        setFeedTitle(next.title);
        setFeedSubtitle(next.subtitle);
      })
      .finally(() => setLoading(false));
  }, [activeSlug, hydration, publicId, effectiveKey]);

  const palette = useMemo(
    () => applyLayoutStyles(resolveAnnouncementPalette(announcements[0]?.theme), layoutSettings),
    [announcements, layoutSettings],
  );

  const pickAnnouncement = (announcement: AnnouncementShape): void => {
    setSelected(announcement);
    if (announcement._id) {
      void trackAnnouncementEvent(hydration, {
        announcementId: announcement._id,
        eventType: 'shown',
        ...resolveEventIdentity(hydration),
        meta: buildEventMeta(hydration),
      });
    }
  };

  const trackCtaClick = (announcement: AnnouncementShape): void => {
    bridge.send('chirp:announcement:clicked', {
      id: announcement.publicId ?? '',
      internalId: announcement._id,
      ctaUrl: announcement.cta?.url,
    });
    if (!announcement._id) return;
    void trackAnnouncementEvent(hydration, {
      announcementId: announcement._id,
      eventType: 'clicked',
      ...resolveEventIdentity(hydration),
      meta: buildEventMeta(hydration),
    });
  };

  const dismiss = (): void => {
    if (feedDismissedRef.current) return;
    feedDismissedRef.current = true;
    // Dedicated event for the changelog/collection feed — distinct from
    // single-announcement dismissal, which always carries the id of the
    // dismissed item.
    bridge.send('chirp:changelog:dismissed', undefined as never);
  };

  // Not-found render path for explicit collection opens that resolved empty.
  // Mirrors the single-announcement notFound state — replaces the empty
  // FeedView ("No announcements yet") with a clear "this collection is no
  // longer available" message so the user isn't confused by an empty drawer
  // that fired an error event.
  const collectionMissing = !loading && mode === 'collection' && announcements.length === 0;

  const feedContent = collectionMissing ? (
    <div style={{ padding: '32px 28px', textAlign: 'center', color: palette.textMuted }}>
      <p style={{ margin: 0, fontSize: 15, color: palette.text }}>
        This collection is no longer available.
      </p>
    </div>
  ) : selected ? (
    <SingleAnnouncementBody
      palette={palette}
      announcement={selected}
      showBackButton
      onBack={() => setSelected(null)}
      onDismiss={dismiss}
      onCtaClick={() => trackCtaClick(selected)}
    />
  ) : (
    <FeedView
      palette={palette}
      announcements={announcements}
      loading={loading}
      activeCollectionSlug={activeSlug}
      showFilters={mode === 'global'}
      onSelectCollection={(slug) => setActiveSlug(slug)}
      onSelectAnnouncement={pickAnnouncement}
      onClose={dismiss}
      title={
        feedTitle ??
        (mode === 'global'
          ? 'Changelog'
          : announcements[0]?.collectionName ?? 'Announcements')
      }
      description={feedSubtitle ?? undefined}
    />
  );

  return renderInShell({
    layout: collectionMissing ? 'modal' : layout ?? 'drawer',
    position,
    palette,
    bridge,
    onDismiss: dismiss,
    children: feedContent,
  });
}

function InlineFeedRenderer({
  bridge,
  publicId,
  hydration,
  layoutSettings,
}: AnnouncementRendererProps): JSX.Element | null {
  const showFilters = layoutSettings?.showFilters !== false;
  const [activeSlug, setActiveSlug] = useState<string | null>(null);
  const effectiveKey = publicId ?? hydration.projectId;
  const initialCached = effectiveKey ? feedCache.get(feedKey(effectiveKey, activeSlug)) : undefined;
  const [announcements, setAnnouncements] = useState<AnnouncementShape[]>(
    initialCached?.result.announcements ?? [],
  );
  const [selected, setSelected] = useState<AnnouncementShape | null>(null);
  const [loading, setLoading] = useState(!initialCached);
  const rootRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!effectiveKey) return;
    const key = feedKey(effectiveKey, activeSlug);
    const cached = feedCache.get(key);
    const isFresh = cached && Date.now() - cached.fetchedAt < FRESH_MS;
    if (cached) {
      setAnnouncements(cached.result.announcements);
      setLoading(false);
    } else {
      setLoading(true);
    }
    if (isFresh) return;
    const identityHints = {
      email: hydration.user?.email,
      uniqueId: hydration.user?.uniqueId,
    };
    const feedInput = publicId
      ? { publicId, collectionSlug: activeSlug ?? undefined, limit: 50, ...identityHints }
      : { clientId: hydration.projectId, collectionSlug: activeSlug ?? undefined, limit: 50, ...identityHints };
    void fetchPublicFeed(hydration, feedInput)
      .then((result) => {
        const next = {
          announcements: (result.announcements ?? []) as AnnouncementShape[],
          title: result.title ?? null,
          subtitle: result.subtitle ?? null,
        };
        feedCache.set(key, { result: next, fetchedAt: Date.now() });
        setAnnouncements(next.announcements);
      })
      .finally(() => setLoading(false));
  }, [activeSlug, hydration, publicId, effectiveKey]);

  // Auto-resize: tell the host iframe our content height so it can size the iframe.
  useEffect(() => {
    const el = rootRef.current;
    if (!el) return;
    const observer = new ResizeObserver((entries) => {
      const height = Math.ceil(entries[0]?.borderBoxSize?.[0]?.blockSize ?? entries[0]?.contentRect.height ?? 0);
      if (height > 0) bridge.send('chirp:resize', { height });
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, [bridge]);

  const palette = useMemo(
    () => applyLayoutStyles(resolveAnnouncementPalette(announcements[0]?.theme), layoutSettings),
    [announcements, layoutSettings],
  );

  const pickAnnouncement = (announcement: AnnouncementShape): void => {
    setSelected(announcement);
    if (announcement._id) {
      void trackAnnouncementEvent(hydration, {
        announcementId: announcement._id,
        eventType: 'shown',
        ...resolveEventIdentity(hydration),
        meta: buildEventMeta(hydration),
      });
    }
  };

  const trackCtaClick = (announcement: AnnouncementShape): void => {
    if (!announcement._id) return;
    void trackAnnouncementEvent(hydration, {
      announcementId: announcement._id,
      eventType: 'clicked',
      ...resolveEventIdentity(hydration),
      meta: buildEventMeta(hydration),
    });
  };

  return (
    <div
      ref={rootRef}
      style={{
        fontFamily: palette.font,
        color: palette.text,
        background: palette.background,
        WebkitFontSmoothing: 'antialiased',
        MozOsxFontSmoothing: 'grayscale',
      }}
    >
      {selected ? (
        <SingleAnnouncementBody
          palette={palette}
          announcement={selected}
          showBackButton
          onBack={() => setSelected(null)}
          onDismiss={() => setSelected(null)}
          onCtaClick={() => trackCtaClick(selected)}
        />
      ) : (
        <FeedView
          palette={palette}
          announcements={announcements}
          loading={loading}
          activeCollectionSlug={activeSlug}
          showFilters={showFilters}
          onSelectCollection={(slug) => setActiveSlug(slug)}
          onSelectAnnouncement={pickAnnouncement}
          onClose={null}
        />
      )}
    </div>
  );
}

function renderInShell({
  layout,
  position,
  palette,
  bridge,
  onDismiss,
  children,
}: {
  layout: AnnouncementLayout;
  position?: AnnouncementPosition;
  palette: ReturnType<typeof resolveAnnouncementPalette>;
  bridge: HostBridge;
  onDismiss: () => void;
  children: ReactNode;
}): JSX.Element {
  switch (layout) {
    case 'drawer':
      return (
        <DrawerShell palette={palette} position={position} onDismiss={onDismiss} bridge={bridge}>
          {children}
        </DrawerShell>
      );
    case 'banner':
      return (
        <BannerShell palette={palette} bridge={bridge} position={position}>
          {children}
        </BannerShell>
      );
    case 'toast':
      return (
        <ToastShell palette={palette} bridge={bridge} position={position}>
          {children}
        </ToastShell>
      );
    case 'modal':
    default:
      return (
        <ModalShell palette={palette} onDismiss={onDismiss} bridge={bridge}>
          {children}
        </ModalShell>
      );
  }
}

// Returns null without a layout slug so the caller can wait for fetch instead of flashing modal.
function resolveLayoutFromAnnouncement(announcement: AnnouncementShape | null): AnnouncementLayout | null {
  return normalizeLayoutSlug(announcement?.layout ?? undefined);
}

function normalizeLayoutSlug(slug: string | undefined): AnnouncementLayout | null {
  const value = slug?.toLowerCase().trim();
  if (!value) return null;
  if (value === 'drawer' || value === 'slideout' || value === 'slide-out' || value === 'sidebar') return 'drawer';
  if (value === 'banner' || value.startsWith('banner-')) return 'banner';
  if (value === 'toast' || value === 'tooltip' || value.startsWith('toast-')) return 'toast';
  if (value === 'modal') return 'modal';
  return null;
}

